import FormControl from '@mui/material/FormControl';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { isArray } from 'lodash';
import React from 'react';
import { LookupFieldConfiguration } from '../../../api/ConfigApi';
import { handleUnauthorizedResponse } from '../../../api/http';
import TabularDatasourcesApi from '../../../api/TabularDatasourcesApi';
import { AuthContext, AuthData } from '../../../context/auth';
import { FieldValues, FieldValuesMap } from '../../../types/policy';
import { arraysEqual } from '../../../util/util';
import FieldLabel from '../../fields/FieldLabel';
import ReactSelect, { SelectValue } from './ReactSelect';

const styles = (theme: any) =>
	createStyles({
		disabledInput: {
			'& .MuiInputBase-root.Mui-disabled': {
				color: 'rgba(0, 0, 0, 0.54)',
			},
		},
	});

interface Props extends WithStyles<typeof styles> {
	labelId: string;
	field: LookupFieldConfiguration;
	value: string;
	allValues: FieldValuesMap;
	isDisabled: boolean;
	readOnly?: boolean;
	loading: boolean;
	userHasWriteAccess: boolean;
	authData: AuthData;
	style: any;
	productName: string;
	configVersion?: string;
	updateLookup: (selectedVal: [string]) => void;
}

interface State {
	options: string[];
	loading: boolean;
}

function getSingleValue(values: FieldValues): string {
	if (isArray(values)) {
		return values[0];
	} else {
		return values;
	}
}

class LookupField extends React.Component<Props, State> {
	constructor(props: any, context: any) {
		super(props);
		const initialOptions = this.props.value ? [this.props.value] : [];
		this.state = {
			options: initialOptions,
			loading: true,
		};
	}

	public componentDidMount() {
		if (!this.props.isDisabled) {
			this.loadAllowedOptions();
		}
	}

	public componentDidUpdate({ allValues, isDisabled }: Props) {
		if (!this.props.isDisabled && this.didDependentValuesChange(allValues)) {
			this.loadAllowedOptions();
		}
		// if lookup field is no longer disabled (example would be editing claim after opening it)
		if (!this.props.isDisabled && isDisabled) {
			this.loadAllowedOptions();
		}
	}

	public render() {
		const {
			loading,
			style,
			field,
			isDisabled,
			readOnly,
			value,
			updateLookup,
			labelId,
			classes,
		} = this.props;
		const { options } = this.state;

		// if field is disabled and has a value, use the value
		const val = options.includes(value) ? value : isDisabled ? value : '';

		if (!loading) {
			return (
				<FormControl style={style}>
					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<FieldLabel
							id={labelId}
							required={!field.optional}
							title={field.title}
						/>
						<ReactSelect
							id={field.name}
							className={classes.disabledInput}
							value={{ value: val, label: val }}
							options={this.state.options.map((v) => ({ value: v, label: v }))}
							onChange={(opt: SelectValue) => updateLookup([opt.value])}
							isDisabled={
								isDisabled ||
								!this.state.options ||
								!this.state.options.length ||
								!!readOnly
							}
							includeEmptyValue
						/>
					</div>
				</FormControl>
			);
		} else {
			return null;
		}
	}

	private loadAllowedOptions = () => {
		const { field, authData, productName, configVersion } = this.props;
		const dependentValues = this.getCurrentDependentValues();

		if (dependentValues != null) {
			this.setState({
				loading: true,
			});

			const pvEnabled =
				authData.auth.configProperties[
					'property.product.versioning.m2.enabled'
				] ||
				authData.auth.configProperties[
					'property.product.versioning.with.repair.enabled'
				];
			// If PV is enabled, we need to pass the configVersion to the API
			// Do not pass config version if PV is OFF
			const passedConfigVersion = pvEnabled ? configVersion : null;

			TabularDatasourcesApi.getAllowedOptionsForTable(
				authData.auth.authorizationToken,
				field.table,
				dependentValues,
				passedConfigVersion,
				productName,
			)
				.then((response: any) => {
					if (response.data) {
						this.setState({
							options: response.data.values.sort(),
							loading: false,
						});
					} else {
						this.setState({ loading: false });
					}
				})
				.catch(async (err) => {
					if (await handleUnauthorizedResponse(err, authData)) {
						return;
					}
					this.setState({
						options: [],
						loading: false,
					});
				});
		} else {
			this.setState({
				options: [],
				loading: false,
			});
		}
	};

	private didDependentValuesChange = (oldValues: FieldValuesMap) => {
		return !arraysEqual(
			this.getCurrentDependentValues(),
			this.getDependentValues(oldValues),
		);
	};

	private getCurrentDependentValues = () => {
		return this.getDependentValues(this.props.allValues);
	};

	private getDependentValues = (values: FieldValuesMap): string[] => {
		const matchFields = this.props.field.match;
		const matchValues = matchFields
			.map((name) => getSingleValue(values[name]))
			.filter((match) => !!match);
		return matchValues.length === matchFields.length ? matchValues : null;
	};
}

function withContext(Component: any) {
	return (props: Partial<Props>) => (
		<AuthContext.Consumer>
			{(authData) => <Component {...props} authData={authData} />}
		</AuthContext.Consumer>
	);
}

export default withStyles(styles, { withTheme: true })(
	withContext(LookupField),
);
